import React, { useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { getNextAdventure, normalizePath } from "@/lib/nextAdventure";
import { useTier } from "@/lib/ageGroup";
import { track } from "@/lib/track";

const CHOICES = {
  learn: { id: "learn", e: "📚", label: "Continue learning", to: "/learn-topics", testid: "next-adventure-learn" },
  play: { id: "play", e: "🎮", label: "Play", to: "/games", testid: "next-adventure-play" },
  explore: { id: "explore", e: "🌍", label: "Explore", to: "/globe", testid: "next-adventure-explore" },
  create: { id: "create", e: "🎨", label: "Create", to: "/coloring", testid: "next-adventure-create" },
  discover: { id: "discover", e: "📖", label: "Discover a story", to: "/stories", testid: "next-adventure-discover" },
  practise: { id: "practise", e: "🎯", label: "Practise", to: "/times-tables-quest", testid: "next-adventure-practise" },
};
/** Up to 3 quiet, contextual choices — never the section the child is already in, never the primary target. */
export function choicesFor(pathname, primaryRoute) {
  const p = pathname || "/";
  let order;
  if (p.startsWith("/games") || p.startsWith("/play")) order = ["learn", "explore", "create"];
  else if (p.startsWith("/country") || p.startsWith("/globe") || p.startsWith("/continents")) order = ["learn", "play", "create"];
  else if (p.startsWith("/stor")) order = ["create", "practise", "explore"];
  else if (p.startsWith("/coloring") || p.startsWith("/pixel") || p.startsWith("/kaleidoscope")) order = ["discover", "explore", "learn"];
  else if (p.startsWith("/learn") || p.startsWith("/lesson") || p.startsWith("/practice") || p.startsWith("/times-tables") || p.startsWith("/language-quest")) order = ["play", "explore", "create"];
  else order = ["learn", "play", "explore"];
  return order.map((k) => CHOICES[k]).filter((c) => c.to !== primaryRoute && !p.startsWith(c.to)).slice(0, 3);
}

/** Shared "What's next?" — one dominant adventure + two quiet choices. Mounted by Layout on child pages. */
export default function NextAdventure({ context = {}, className = "" }) {
  const pathname = normalizePath(useLocation().pathname);
  const tier = useTier() || "explorers";
  const next = useMemo(() => getNextAdventure({ route: pathname, context, tier }), [pathname, context, tier]);
  useEffect(() => { track("next_adventure_impression", { route: pathname, type: next.type, target: next.route }); }, [pathname, next.type, next.route]);
  if (!next || next.route === pathname) return null;
  return (
    <section aria-labelledby="next-adventure-title" data-testid="next-adventure" data-type={next.type} className={`max-w-3xl mx-auto px-4 py-8 ${className}`}>
      <p id="next-adventure-title" className="text-xs font-bold text-slate-600">What's next?</p>
      <div className="mt-2 rounded-3xl border-[3px] border-slate-900 bg-yellow-100 p-4 shadow-[0_4px_0_0_rgba(15,23,42,1)]">
        <h2 className="font-heading text-xl md:text-2xl font-bold leading-tight">{next.title}</h2>
        <p className="text-sm text-slate-700 mt-0.5">{next.description}</p>
        {next.reason && next.reason !== next.description ? <p className="text-xs text-slate-600 italic mt-1">{next.reason}</p> : null}
        <Link to={next.route} onClick={() => track("next_adventure_clicked", { route: pathname, type: next.type, target: next.route })} data-testid="next-adventure-primary"
          className="mt-3 inline-flex items-center gap-1 min-h-[48px] px-6 rounded-full border-[3px] border-slate-900 bg-slate-900 text-white font-heading font-bold shadow-[0_3px_0_0_rgba(15,23,42,0.4)] hover:-translate-y-0.5 transition-transform">
          {next.action} <ChevronRight size={18} aria-hidden />
        </Link>
      </div>
      <div className="mt-2 flex flex-wrap gap-2 text-sm" data-testid="next-adventure-choices">
        {choicesFor(pathname, next.route).map((c) => (
          <Link key={c.to} to={c.to} data-testid={`next-adventure-choice-${c.id}`} onClick={() => track("whats_next_choice_clicked", { route: pathname, choice: c.label, target: c.to })} className="min-h-[44px] inline-flex items-center gap-1 px-4 rounded-full border-2 border-slate-900 bg-white font-bold hover:bg-yellow-100"><span aria-hidden>{c.e}</span> {c.label}</Link>
        ))}
        <Link to="/everything" data-testid="next-adventure-else" className="min-h-[44px] inline-flex items-center px-4 rounded-full border-2 border-slate-900 bg-white font-bold">Explore everything</Link>
      </div>
    </section>
  );
}
